import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const { staff, student, logout, loading } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const handleLogout = async () => {
    try { await logout(); } catch (err) { }
  };

  if (loading) {
    return <header className="fixed top-0 left-0 right-0 z-50 h-16" style={{ backgroundColor: '#1a5c38' }} />;
  }

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { path: '/', label: 'Home', icon: 'fas fa-home' },
    { path: '/about', label: 'About', icon: 'fas fa-info-circle' },
    { path: '/academics', label: 'Academics', icon: 'fas fa-book-open' },
    { path: '/admission', label: 'Admissions', icon: 'fas fa-user-plus' },
    { path: '/news', label: 'News', icon: 'fas fa-newspaper' },
    { path: '/gallery', label: 'Gallery', icon: 'fas fa-images' },
    { path: '/contact', label: 'Contact', icon: 'fas fa-envelope' },
  ];

  const authItems = [
    { path: '/auth/student-register', label: 'Register', icon: 'fas fa-user-plus' },
    { path: '/auth/student-login', label: 'Student Portal', icon: 'fas fa-user-graduate' },
    { path: '/auth/staff-login', label: 'Staff Login', icon: 'fas fa-user-tie' },
  ];

  const getHeaderBg = () => scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-[#1a5c38]';
  const getLogoColor = () => scrolled ? 'text-[#1a5c38]' : 'text-white';

  return (
    <>
      <header className={'fixed top-0 left-0 right-0 z-50 transition-all duration-300 ' + getHeaderBg()}>
        <div className="container mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link
              to="/"
              className={'flex items-center gap-2.5 font-bold text-lg lg:text-xl tracking-tight transition-colors ' + getLogoColor()}
              onClick={() => setMenuOpen(false)}
            >
              <i className="fas fa-mosque text-xl lg:text-2xl"></i>
              <span className="hidden sm:inline">Dar-l-Hikmah College</span>
              <span className="sm:hidden">Dar-l-Hikmah</span>
            </Link>

            {/* Desktop Center Nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => {
                const active = isActive(item.path);
                let linkClass = 'relative px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 outline-none focus:ring-2 focus:ring-[#c9a84c]/50 ';
                if (active) {
                  linkClass += scrolled ? 'text-[#1a5c38]' : 'text-white';
                } else {
                  linkClass += scrolled ? 'text-slate-600 hover:text-[#1a5c38] hover:bg-[#1a5c38]/5' : 'text-white/80 hover:text-white hover:bg-white/10';
                }
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={linkClass}
                    aria-current={active ? 'page' : undefined}
                  >
                    {item.label}
                    {active && (
                      <motion.span
                        layoutId="activeNav"
                        className="absolute bottom-0 left-2 right-2 h-0.5 bg-[#c9a84c] rounded-full"
                        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                      />
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Desktop Right Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              {staff && (
                <>
                  <Link to="/staff/dashboard" className={'text-sm font-medium px-3 py-2 rounded-md transition-colors ' + (scrolled ? 'text-[#1a5c38] hover:bg-[#1a5c38]/5' : 'text-white/90 hover:bg-white/10')}>
                    <i className="fas fa-user-tie mr-1.5"></i>{staff.name}
                  </Link>
                  <button onClick={handleLogout} className="text-sm font-medium px-3 py-2 rounded-md text-red-500 hover:bg-red-50 transition-colors">
                    Logout
                  </button>
                </>
              )}
              {student && (
                <>
                  <Link to="/student/dashboard" className={'text-sm font-medium px-3 py-2 rounded-md transition-colors ' + (scrolled ? 'text-[#1a5c38] hover:bg-[#1a5c38]/5' : 'text-white/90 hover:bg-white/10')}>
                    <i className="fas fa-user-graduate mr-1.5"></i>{student.name}
                  </Link>
                  <button onClick={handleLogout} className="text-sm font-medium px-3 py-2 rounded-md text-red-500 hover:bg-red-50 transition-colors">
                    Logout
                  </button>
                </>
              )}
              {!staff && !student && (
                <>
                  <Link to="/auth/student-register" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-[#c9a84c] text-white hover:bg-[#b08d3a] transition-colors shadow-sm">
                    <i className="fas fa-user-plus"></i>Register
                  </Link>
                  <Link to="/auth/student-login" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-[#1a5c38] text-white hover:bg-[#144a2d] transition-colors shadow-sm">
                    <i className="fas fa-user-graduate"></i>Student Portal
                  </Link>
                  <Link to="/auth/staff-login" className={'inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg border-2 transition-colors ' + (scrolled ? 'border-[#1a5c38] text-[#1a5c38] hover:bg-[#1a5c38] hover:text-white' : 'border-white text-white hover:bg-white hover:text-[#1a5c38]')}>
                    <i className="fas fa-user-tie"></i>Staff Login
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Hamburger */}
            <button
              className={'lg:hidden p-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[#c9a84c]/50 ' + (scrolled ? 'text-[#1a5c38]' : 'text-white')}
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              aria-expanded={menuOpen}
            >
              <i className="fas fa-bars text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Slide-in Drawer */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm lg:hidden"
              onClick={() => setMenuOpen(false)}
              aria-hidden="true"
            />

            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 bottom-0 z-[70] w-[280px] max-w-[85vw] bg-white shadow-2xl lg:hidden flex flex-col"
              role="dialog"
              aria-modal="true"
              aria-label="Navigation menu"
            >
              {/* Drawer Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                <span className="font-bold text-[#1a5c38] text-lg">
                  <i className="fas fa-mosque mr-2"></i>Menu
                </span>
                <button
                  onClick={() => setMenuOpen(false)}
                  className="p-2 rounded-md text-slate-500 hover:text-[#1a5c38] hover:bg-[#1a5c38]/5 transition-colors focus:outline-none focus:ring-2 focus:ring-[#c9a84c]/50"
                  aria-label="Close menu"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>

              {/* Drawer Nav Links */}
              <nav className="flex-1 overflow-y-auto py-3">
                {navItems.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMenuOpen(false)}
                      className={'flex items-center gap-3 px-5 py-3.5 text-sm font-medium transition-colors outline-none focus:bg-[#1a5c38]/5 focus:ring-2 focus:ring-inset focus:ring-[#c9a84c]/30 ' + (active ? 'text-[#1a5c38] bg-[#1a5c38]/5 border-r-4 border-[#c9a84c]' : 'text-slate-600 hover:text-[#1a5c38] hover:bg-slate-50')}
                      aria-current={active ? 'page' : undefined}
                    >
                      <i className={item.icon + ' w-5 text-center ' + (active ? 'text-[#c9a84c]' : 'text-slate-400')}></i>
                      {item.label}
                    </Link>
                  );
                })}

                <div className="mx-5 my-3 border-t border-slate-100"></div>

                {staff && (
                  <>
                    <Link to="/staff/dashboard" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-5 py-3.5 text-sm font-medium text-slate-600 hover:text-[#1a5c38] hover:bg-slate-50 transition-colors">
                      <i className="fas fa-tachometer-alt w-5 text-center text-slate-400"></i>Staff Dashboard
                    </Link>
                    <button onClick={() => { handleLogout(); setMenuOpen(false); }} className="w-full flex items-center gap-3 px-5 py-3.5 text-sm font-medium text-red-500 hover:bg-red-50 transition-colors">
                      <i className="fas fa-sign-out-alt w-5 text-center"></i>Logout
                    </button>
                  </>
                )}
                {student && (
                  <>
                    <Link to="/student/dashboard" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-5 py-3.5 text-sm font-medium text-slate-600 hover:text-[#1a5c38] hover:bg-slate-50 transition-colors">
                      <i className="fas fa-tachometer-alt w-5 text-center text-slate-400"></i>Student Dashboard
                    </Link>
                    <button onClick={() => { handleLogout(); setMenuOpen(false); }} className="w-full flex items-center gap-3 px-5 py-3.5 text-sm font-medium text-red-500 hover:bg-red-50 transition-colors">
                      <i className="fas fa-sign-out-alt w-5 text-center"></i>Logout
                    </button>
                  </>
                )}
                {!staff && !student && (
                  <>
                    <Link to="/auth/student-register" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-5 py-3.5 text-sm font-medium text-[#c9a84c] hover:bg-[#c9a84c]/5 transition-colors">
                      <i className="fas fa-user-plus w-5 text-center"></i>Register
                    </Link>
                    <Link to="/auth/student-login" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-5 py-3.5 text-sm font-medium text-[#1a5c38] hover:bg-[#1a5c38]/5 transition-colors">
                      <i className="fas fa-user-graduate w-5 text-center"></i>Student Portal
                    </Link>
                    <Link to="/auth/staff-login" onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-5 py-3.5 text-sm font-medium text-slate-600 hover:text-[#1a5c38] hover:bg-slate-50 transition-colors">
                      <i className="fas fa-user-tie w-5 text-center"></i>Staff Login
                    </Link>
                  </>
                )}
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Spacer for fixed header */}
      <div className="h-16 lg:h-20"></div>
    </>
  );
};

export default Header;
